/**
 * Born-scored publish gate — pure threshold + gate logic for the Content
 * workspace's Publish action.
 *
 * The discipline: NO content publishes unscored. The Publish action stays
 * disabled until the draft has an AQI read; below the bar (default 75,
 * override via VITE_PUBLISH_AQI_THRESHOLD) it becomes an explicit
 * "Publish anyway (below bar)" requiring an extra confirm with the score
 * shown. Either path only QUEUES a `publish_content` proposal — the
 * approval inbox is the final gate before the engine is ever called.
 *
 * Pure logic + env read only: no store, no network, no DOM.
 */

/** The default AQI bar a draft must clear to publish without the extra confirm. */
export const DEFAULT_PUBLISH_AQI_THRESHOLD = 75;

/**
 * The active AQI publish threshold. Reads VITE_PUBLISH_AQI_THRESHOLD at call
 * time (testable via stubbed env); anything unset/invalid/out-of-range falls
 * back to the default 75.
 */
export function publishAqiThreshold(): number {
  const raw = import.meta.env.VITE_PUBLISH_AQI_THRESHOLD as string | undefined;
  if (raw == null || String(raw).trim() === '') return DEFAULT_PUBLISH_AQI_THRESHOLD;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n > 100) return DEFAULT_PUBLISH_AQI_THRESHOLD;
  return Math.round(n);
}

/** The three honest gate states for a draft's Publish action. */
export type PublishGate =
  | {
      state: 'unscored';
      canPublish: false;
      belowThreshold: false;
      requiresExtraConfirm: false;
      threshold: number;
      aqiTotal: null;
      reason: string;
    }
  | {
      state: 'below_bar';
      canPublish: true;
      belowThreshold: true;
      requiresExtraConfirm: true;
      threshold: number;
      aqiTotal: number;
      reason: string;
    }
  | {
      state: 'ready';
      canPublish: true;
      belowThreshold: false;
      requiresExtraConfirm: false;
      threshold: number;
      aqiTotal: number;
      reason: string;
    };

/**
 * Evaluate the born-scored gate for a draft.
 *   - `aqiTotal == null` (unscored) → Publish DISABLED. Nothing publishes unscored.
 *   - `aqiTotal < threshold`        → allowed, but flagged belowThreshold and
 *                                     requiring the extra "publish anyway" confirm.
 *   - `aqiTotal >= threshold`       → ready; the normal Publish path.
 */
export function evaluatePublishGate(
  aqiTotal: number | null | undefined,
  threshold: number = publishAqiThreshold(),
): PublishGate {
  if (aqiTotal == null) {
    return {
      state: 'unscored',
      canPublish: false,
      belowThreshold: false,
      requiresExtraConfirm: false,
      threshold,
      aqiTotal: null,
      reason: 'Score the draft first — nothing publishes unscored.',
    };
  }
  if (aqiTotal < threshold) {
    return {
      state: 'below_bar',
      canPublish: true,
      belowThreshold: true,
      requiresExtraConfirm: true,
      threshold,
      aqiTotal,
      reason: `AQI ${aqiTotal} is below the ${threshold} bar — publishing anyway needs an explicit confirm.`,
    };
  }
  return {
    state: 'ready',
    canPublish: true,
    belowThreshold: false,
    requiresExtraConfirm: false,
    threshold,
    aqiTotal,
    reason: `AQI ${aqiTotal} clears the ${threshold} bar.`,
  };
}

/** Button label for the gate state — the below-bar path is named honestly. */
export function publishButtonLabel(gate: PublishGate): string {
  return gate.state === 'below_bar' ? 'Publish anyway (below bar)' : 'Publish';
}
